/**
 * seenBy — who has read what in a thread, from receipts (2026-09-08, PR-3;
 * per-message marks 2026-09-10). Pure: the host renders the result; nothing
 * here touches Firestore.
 *
 * A READER is a participant uid or `'assistant'` (Prest0n's server-written
 * receipt — never a uid, so it can never collide). A receipt carries a
 * high-water mark — `lastReadOrderTs`: the reader has read every message at
 * or below it — and, since 2026-09-10, `seen`: the reader's OWN per-message
 * marks (shared message anchor → orderTs) for the messages they actually
 * lingered on (Miles: "on all messages … if they've had open to that
 * particular message for more than 2 seconds"; clarified: only messages
 * actually lingered on).
 *
 * Two questions the UI asks:
 *   • `seenByRows` — for each message, WHICH readers' marks sit under it. Per
 *     message (`perMessage`): a human with a `seen` map marks exactly the
 *     messages in it (never their own); a human whose receipt predates the
 *     map keeps the legacy high-water row (one mark under the last message at
 *     or below their mark) until they linger again; Prest0n marks every human
 *     message at or below its mark — it read them all when it answered.
 *     Without `perMessage` the legacy rule applies to everyone (the lever).
 *   • `readersOf` — for one message, who has read it (the accent ✓✓ in a
 *     direct message, the tooltip list): exact from `seen` when present, the
 *     mark otherwise.
 *
 * A message's order is its `orderTs` (the shared-store key); a local bubble
 * without one falls back to its timestamp — same instant, same clock. A
 * message is keyed in `seen` by its SHARED anchor (`anchorFor`, the id every
 * participant agrees on — `message.id` differs per viewer for own bubbles);
 * without an `anchorFor` the message id is the key.
 */
import type { ChatMessage } from '../../components/GlobalChatSystem/types';

export const ASSISTANT_RECEIPT_ID = 'assistant';

/** The per-message map is bounded; the client evicts the oldest marks past this. */
export const SEEN_MAP_CAP = 400;
/** The rules refuse a removal below this size, so a trim is only ever an eviction at capacity. */
export const SEEN_EVICT_FLOOR = 300;

export interface ReceiptFacts {
  /** The reader has read every message with orderTs <= this. */
  lastReadOrderTs: number;
  lastReadMessageId?: string;
  /** The reader's own per-message marks: shared anchor → the message's orderTs. Absent on receipts written before 2026-09-10. */
  seen?: Record<string, number>;
}

/** reader id (uid or 'assistant') → their receipt. */
export type ReceiptsByReader = Record<string, ReceiptFacts>;

type MessageLike = Pick<ChatMessage, 'id' | 'isUser' | 'senderId' | 'orderTs' | 'timestamp' | 'isSystem'>;

/** The instant a message orders by: `orderTs`, else its timestamp; null when undatable. */
export function messageOrderTs(m: MessageLike | null | undefined): number | null {
  if (!m) return null;
  if (typeof m.orderTs === 'number' && Number.isFinite(m.orderTs)) return m.orderTs;
  const t = m.timestamp as unknown;
  if (t instanceof Date) {
    const ms = t.getTime();
    return Number.isFinite(ms) ? ms : null;
  }
  if (typeof t === 'string' || typeof t === 'number') {
    const ms = new Date(t).getTime();
    return Number.isFinite(ms) ? ms : null;
  }
  return null;
}

/** Who sent a message, as a reader id: the sender uid, 'assistant' for Prest0n, '' when unknown. */
export function messageAuthorId(m: MessageLike | null | undefined): string {
  if (!m) return '';
  if (m.isUser !== true) return ASSISTANT_RECEIPT_ID;
  return typeof m.senderId === 'string' ? m.senderId.trim() : '';
}

function validReceipt(r: ReceiptFacts | undefined): r is ReceiptFacts {
  return !!r && typeof r.lastReadOrderTs === 'number' && Number.isFinite(r.lastReadOrderTs) && r.lastReadOrderTs >= 0;
}

/** The reader's per-message map when the receipt carries one (a map, possibly empty), else null. */
function seenMapOf(r: ReceiptFacts): Record<string, number> | null {
  return r.seen && typeof r.seen === 'object' && !Array.isArray(r.seen) ? r.seen : null;
}

export interface SeenByOptions {
  /** The viewer — their own receipt is never rendered. */
  selfUid: string | null | undefined;
  /**
   * Readers allowed to appear: the thread's human participants. `'assistant'`
   * is always admitted unless `assistantEnabled === false`. When omitted, every
   * receipt in the map counts (the store is participant-gated server-side).
   */
  participantIds?: ReadonlyArray<string> | null;
  assistantEnabled?: boolean;
  /**
   * Per-message marks (2026-09-10): a human with a `seen` map marks exactly
   * the messages in it; Prest0n marks every human message at or below its
   * mark. Off (absent) = the legacy high-water row for everyone.
   */
  perMessage?: boolean;
  /** The shared anchor a message is keyed by in `seen`; defaults to the message id. */
  anchorFor?: (m: MessageLike) => string | null | undefined;
}

function admittedReaders(receipts: ReceiptsByReader, opts: SeenByOptions): string[] {
  const self = (opts.selfUid ?? '').trim();
  const allow = opts.participantIds ? new Set(opts.participantIds.map((p) => String(p ?? '').trim()).filter(Boolean)) : null;
  const out: string[] = [];
  for (const [reader, facts] of Object.entries(receipts ?? {})) {
    const id = (reader ?? '').trim();
    if (!id || id === self) continue;
    if (!validReceipt(facts)) continue;
    if (id === ASSISTANT_RECEIPT_ID) {
      if (opts.assistantEnabled === false) continue;
    } else if (allow && !allow.has(id)) {
      continue;
    }
    out.push(id);
  }
  return out;
}

function anchorOf(m: MessageLike, opts: SeenByOptions): string {
  const raw = opts.anchorFor ? opts.anchorFor(m) : m.id;
  return typeof raw === 'string' ? raw.trim() : '';
}

/** Readers whose receipt covers `message` (never its author, never the viewer). */
export function readersOf(
  message: MessageLike | null | undefined,
  receipts: ReceiptsByReader,
  opts: SeenByOptions,
): string[] {
  const at = messageOrderTs(message);
  if (at === null || !message || message.isSystem) return [];
  const author = messageAuthorId(message);
  const anchor = opts.perMessage ? anchorOf(message, opts) : '';
  return admittedReaders(receipts, opts).filter((r) => {
    if (r === author) return false;
    const facts = receipts[r];
    if (opts.perMessage && r !== ASSISTANT_RECEIPT_ID) {
      const marks = seenMapOf(facts);
      if (marks) return !!anchor && Object.prototype.hasOwnProperty.call(marks, anchor);
    }
    return facts.lastReadOrderTs >= at;
  });
}

/**
 * message id → readers whose mark sits under it. Legacy (no `perMessage`):
 * for each reader, the last message (transcript order) at or below their mark
 * that they did not author. Per message: see the module comment. Messages
 * without a usable id or instant, and system rows, never carry one.
 */
export function seenByRows(
  messages: ReadonlyArray<MessageLike>,
  receipts: ReceiptsByReader,
  opts: SeenByOptions,
): Map<string, string[]> {
  const rows = new Map<string, string[]>();
  const readers = admittedReaders(receipts, opts);
  if (readers.length === 0 || !Array.isArray(messages) || messages.length === 0) return rows;
  const push = (id: string, reader: string): void => {
    const list = rows.get(id) ?? [];
    list.push(reader);
    rows.set(id, list);
  };
  for (const reader of readers) {
    const facts = receipts[reader];
    const mark = facts.lastReadOrderTs;
    if (opts.perMessage && reader === ASSISTANT_RECEIPT_ID) {
      // Prest0n read every human message at or below the turn it answered.
      for (const m of messages) {
        if (!m || m.isSystem) continue;
        const id = typeof m.id === 'string' ? m.id.trim() : '';
        if (!id) continue;
        const at = messageOrderTs(m);
        if (at === null || at > mark) continue;
        if (messageAuthorId(m) === reader) continue;
        push(id, reader);
      }
      continue;
    }
    const marks = opts.perMessage ? seenMapOf(facts) : null;
    if (marks) {
      // Exactly the messages this reader lingered on.
      for (const m of messages) {
        if (!m || m.isSystem) continue;
        const id = typeof m.id === 'string' ? m.id.trim() : '';
        if (!id) continue;
        if (messageAuthorId(m) === reader) continue;
        const anchor = anchorOf(m, opts);
        if (!anchor || !Object.prototype.hasOwnProperty.call(marks, anchor)) continue;
        push(id, reader);
      }
      continue;
    }
    // Legacy high-water row: the last message at or below the mark.
    let target: string | null = null;
    for (const m of messages) {
      if (!m || m.isSystem) continue;
      const id = typeof m.id === 'string' ? m.id.trim() : '';
      if (!id) continue;
      const at = messageOrderTs(m);
      if (at === null || at > mark) continue;
      if (messageAuthorId(m) === reader) continue;
      target = id; // later messages in transcript order replace earlier ones
    }
    if (target) push(target, reader);
  }
  return rows;
}

/**
 * The receipt the viewer writes after dwelling on a thread: the newest
 * datable, non-system message in the transcript (their own included — they
 * certainly saw those). Null when nothing is datable.
 */
export function receiptForTranscript(messages: ReadonlyArray<MessageLike>): ReceiptFacts | null {
  let best: { at: number; id: string } | null = null;
  for (const m of messages ?? []) {
    if (!m || m.isSystem) continue;
    const at = messageOrderTs(m);
    if (at === null) continue;
    const id = typeof m.id === 'string' ? m.id.trim() : '';
    if (!best || at >= best.at) best = { at, id };
  }
  if (!best) return null;
  return best.id ? { lastReadOrderTs: best.at, lastReadMessageId: best.id } : { lastReadOrderTs: best.at };
}

/** One per-message mark the viewer earned by lingering: the shared anchor and the message's instant. */
export interface SeenMark {
  id: string;
  orderTs: number;
}

export interface MergedSeenMarks {
  /** The map after the adds and the eviction, ready to store. */
  seen: Record<string, number>;
  /** Anchors evicted to stay under the cap (oldest instants first). */
  evictedIds: string[];
  /** The newest instant in the map (the high-water mark it implies), or null for an empty map. */
  maxOrderTs: number | null;
  /** The anchor of that newest instant, or null. */
  maxId: string | null;
}

/**
 * Merge new marks into the stored map, then evict the OLDEST (lowest instant)
 * down to the cap. Pure. An existing entry keeps its stored instant (the rules
 * refuse a changed value); junk (a non-finite or negative instant, an empty
 * id) is ignored.
 */
export function mergeSeenMarks(
  prev: Readonly<Record<string, number>> | null | undefined,
  adds: ReadonlyArray<SeenMark>,
  cap: number = SEEN_MAP_CAP,
): MergedSeenMarks {
  const seen: Record<string, number> = {};
  for (const [k, v] of Object.entries(prev ?? {})) {
    const id = (k ?? '').trim();
    if (id && typeof v === 'number' && Number.isFinite(v) && v >= 0) seen[id] = Math.floor(v);
  }
  for (const mark of adds ?? []) {
    const id = (mark?.id ?? '').trim();
    if (!id || typeof mark.orderTs !== 'number' || !Number.isFinite(mark.orderTs) || mark.orderTs < 0) continue;
    if (Object.prototype.hasOwnProperty.call(seen, id)) continue;
    seen[id] = Math.floor(mark.orderTs);
  }
  const evictedIds: string[] = [];
  const ids = Object.keys(seen);
  if (ids.length > cap) {
    ids.sort((a, b) => seen[a] - seen[b] || (a < b ? -1 : a > b ? 1 : 0));
    for (const id of ids.slice(0, ids.length - cap)) {
      evictedIds.push(id);
      delete seen[id];
    }
  }
  let maxOrderTs: number | null = null;
  let maxId: string | null = null;
  for (const [id, at] of Object.entries(seen)) {
    if (maxOrderTs === null || at > maxOrderTs) { maxOrderTs = at; maxId = id; }
  }
  return { seen, evictedIds, maxOrderTs, maxId };
}

/**
 * Prest0n's receipt from the server's side (parity helper — the server stamps
 * `lastReadOrderTs = the human anchor's orderTs`): every human message at or
 * before the turn Prest0n answered has been read by Prest0n.
 */
export function isReadByAssistant(message: MessageLike | null | undefined, receipts: ReceiptsByReader): boolean {
  const r = receipts?.[ASSISTANT_RECEIPT_ID];
  const at = messageOrderTs(message);
  if (!validReceipt(r) || at === null || !message || message.isUser !== true) return false;
  return r.lastReadOrderTs >= at;
}
